"use client";

import React, { useEffect, useRef, useState } from "react";
import styles from "./Picker.module.scss";

const VIEW_TYPE = {
  mobile: { padding: 80, height: 28 },
  desktop: { padding: 80, height: 35 },
};

type option_value = number | string | boolean;

interface option {
  value: option_value;
  label: string;
}

interface PickerProps {
  setOption?: (value: option_value) => void;
  options: option[];
  initialValue?: option_value;
  fullWidth?: boolean;
}

const Picker: React.FC<PickerProps> = ({ setOption, options = [], initialValue, fullWidth = true }) => {
  const [selectedValue, setSelectedValue] = useState<option_value>();

  // Refs for each scrollable column
  const columnRef = useRef(null);

  useEffect(() => {
    const selectedIndex = options.findIndex(({ value }) => value === initialValue);
    const selectedOption = options[Math.max(selectedIndex, 0)];
    setSelectedValue(selectedOption.value);
    columnRef.current.scrollTop = VIEW_TYPE.mobile.height * (selectedIndex + 1);
  }, []);

  useEffect(() => {
    if (setOption && typeof setOption === "function") {
      setOption(selectedValue);
    }
  }, [selectedValue]);

  const getCorrectItem = (index: number): option => {
    const length = options.length;

    if (index > length - 1) {
      return options[length - 1];
    }

    if (index < 0) {
      return options[0];
    }

    return options[index];
  };

  // Calculate selected item based on scroll position
  const handleScroll = (): void => {
    const scrollTop = columnRef.current.scrollTop - VIEW_TYPE.mobile.padding;
    const itemHeight = columnRef.current.clientHeight / 5;
    const index = Math.round((scrollTop + itemHeight * 2.5) / itemHeight);
    const selectedOption = getCorrectItem(index);
    setSelectedValue(selectedOption.value);
  };

  const handleClick = (index: number): void => {
    const scrollTo = VIEW_TYPE.mobile.height * (index + 1);
    columnRef.current.scrollTo({ top: scrollTo, behavior: "smooth" });
  };

  return (
    <div className={`${styles.picker} ${!fullWidth ? styles.fitContent : ""}`}>
      <div className={styles.column} ref={columnRef} onScroll={handleScroll}>
        {options.map(({ value, label }: option, index: number) => (
          <div
            onClick={(): void => handleClick(index)}
            key={index}
            id={`day-${index}`}
            className={`${styles.item} ${value === selectedValue ? styles.selected : ""}`}
          >
            {label}
          </div>
        ))}
      </div>
    </div>
  );
};

export default Picker;
